<script setup>
import { dailyHistoryStage } from '@/utils/dailyHistoryStage.js'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { projectWorkspaceApi, materialAssetsApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'
import { isRecoverableDispatchConflict } from '@/utils/dailyDispatchRecovery.js'
import { buildExactDispatchScope, isRecentlyOnline } from '@/utils/dailyDispatchTargets.js'
import { batchPreparationHeadline, preparationTimingText, canRetryCloudPreparation, contentProgressFieldStatuses, contentProgressLabel, contentProgressPercent, contentProgressStageLabel, contentProgressText, isRecoveringPreparation, reportPreparationDiagnostics, shouldPresentPreparationFailure } from '@/utils/dailyPreparationPresentation.js'
import { createLatestReadGate } from '@/utils/latestReadGate.js'
import DispatchSchedulesPanel from '@/components/DispatchSchedulesPanel.vue'
import DispatchOccurrenceHistory from '@/components/DispatchOccurrenceHistory.vue'
import DispatchBindingReadiness from '@/components/DispatchBindingReadiness.vue'
import DailyDispatchRecovery from '@/components/DailyDispatchRecovery.vue'
import PublicationDataPanel from '@/components/PublicationDataPanel.vue'
import PublicationRatesPanel from '@/components/PublicationRatesPanel.vue'
import { phoneRecoveryLabel, phoneIssueTitle } from '@/utils/phoneTaskRecovery.js'

const loading = ref(true)
const refreshing = ref(false)
const dispatching = ref(false)
const retryingBatch = ref(false)
const stoppingBatch = ref(false)
const reviewingContentTaskId = ref('')
const reviewingProofId = ref('')
const errorMessage = ref('')
const stateMessage = ref('')
const sessionExpired = ref(false)
const overview = ref(null)
const savedHistory = new URLSearchParams(window.location.search)
const historyDate = ref(savedHistory.get('task_date') || shanghaiToday())
const historyBatch = ref(savedHistory.get('task_batch') || '')
const historyFrom = ref(savedHistory.get('history_from') || '')
const historyTo = ref(savedHistory.get('history_to') || '')
const historyMode = ref(savedHistory.get('history_mode') || '')
const historyOffset = ref(0)
const historyResult = ref({ items: [], total: 0 })
const historyBusy = ref(false)
const historyError = ref('')
const assignments = ref([])
const policy = ref(null)
const policySaving = ref(false)
const autoDispatchTime = ref('09:00')
const dispatchPlan = ref(null)
const dispatchScopeSnapshot = ref(null)
const dispatchSelectionOpen = ref(false)
const selectedAssignmentIds = ref([])
const materialPreflight = ref(null)
const materialChoices = ref([])
const selectedMaterialIds = ref([])
const expandedPersonaId = ref('')
const personaTasks = ref([])
const taskPage = ref(1)
const taskPageSize = 50
const searchText = ref('')
const statusFilter = ref('all')
const tenantId = computed(() => authStore.state.selectedTenant?.id || '')
const project = computed(() => authStore.state.assignment?.project || null)
const metrics = computed(() => overview.value?.metrics || {})
const batchContentProgress = computed(() => ({
  content_passed_units: metrics.value.content_passed_units,
  content_total_units: metrics.value.content_total_units,
  details_complete: metrics.value.content_details_complete,
}))
const groups = computed(() => Array.isArray(overview.value?.persona_groups) ? overview.value.persona_groups : [])
const policyBlockerMessage = computed(() => policy.value?.blocker?.message || policy.value?.configuration_notice?.message || '')
const activeAssignments = computed(() => assignments.value.filter((item) => item.status === 'active'))
const selectedAssignments = computed(() => activeAssignments.value.filter((item) => selectedAssignmentIds.value.includes(String(item.id))))
const onlineAssignments = computed(() => activeAssignments.value.filter((item) => isRecentlyOnline(item)))
const isBatchActive = computed(() => ['preparing', 'slow', 'attention', 'ready', 'failed'].includes(overview.value?.batch_status))
const isBatchTerminal = computed(() => ['completed', 'stopped', 'empty'].includes(overview.value?.batch_status))
const shouldPollBatch = computed(() => ['preparing', 'slow', 'attention', 'ready'].includes(overview.value?.batch_status)
  || (overview.value?.batch_status === 'failed' && groups.value.some(isRecoveringPreparation)))
const canStopBatch = computed(() => Boolean(overview.value?.dispatch_batch_id)
  && isBatchActive.value
  && (overview.value?.batch_control?.state || 'active') === 'active')
const filteredGroups = computed(() => {
  const query = searchText.value.trim().toLowerCase()
  return groups.value.filter((group) => {
    const matchesSearch = !query || String(group.display_name || '').toLowerCase().includes(query)
    const matchesStatus = statusFilter.value === 'all'
      || (statusFilter.value === 'problem' && ['failed', 'retry_wait', 'attention'].includes(group.copy_status))
      || group.copy_status === statusFilter.value
    return matchesSearch && matchesStatus
  })
})
let batchTimer = null
let disposed = false
let overviewPollFailures = 0
let lastPolicyPollAt = 0
const policyReads = createLatestReadGate()
const overviewReads = createLatestReadGate()
const pageReads = createLatestReadGate()
const personaReads = createLatestReadGate()
const historyReads = createLatestReadGate()
function overviewScope() { return JSON.stringify([tenantId.value, project.value?.id, historyDate.value, historyBatch.value]) }
function personaScope() { return JSON.stringify([overviewScope(), expandedPersonaId.value, overview.value?.dispatch_batch_id]) }

function saveHistorySelection() {
  const url = new URL(window.location.href)
  for (const [key, value] of Object.entries({ task_date: historyDate.value, task_batch: historyBatch.value,
    history_from: historyFrom.value, history_to: historyTo.value, history_mode: historyMode.value })) {
    if (value) url.searchParams.set(key, value)
    else url.searchParams.delete(key)
  }
  window.history.replaceState(window.history.state, '', url)
}
async function loadHistory(offset = historyOffset.value) {
  if (!project.value?.id || !tenantId.value || sessionExpired.value) return
  const currentScope = () => JSON.stringify([tenantId.value, project.value?.id, historyFrom.value, historyTo.value, historyMode.value, offset])
  const scope = currentScope()
  const ticket = historyReads.begin(scope)
  historyBusy.value = true
  historyError.value = ''
  try {
    const result = await projectWorkspaceApi.dailyTaskBatches(tenantId.value, project.value.id, {
      date_from: historyFrom.value, date_to: historyTo.value, dispatch_mode: historyMode.value, limit: 20, offset,
    })
    if (!disposed && historyReads.accepts(ticket, currentScope()) && !sessionExpired.value) {
      historyResult.value = result
      historyOffset.value = offset
      saveHistorySelection()
    }
  } catch (error) {
    if (historyReads.accepts(ticket, currentScope())) historyError.value = error?.message || '历史任务读取失败，请重试。'
  } finally {
    if (historyReads.accepts(ticket, currentScope())) historyBusy.value = false
  }
}
async function selectHistory(batch) {
  historyDate.value = batch.local_date
  historyBatch.value = batch.dispatch_batch_id || ''
  overviewReads.invalidate()
  pageReads.invalidate()
  personaReads.invalidate()
  overview.value = null
  expandedPersonaId.value = ''
  personaTasks.value = []
  taskPage.value = 1
  saveHistorySelection()
  await load()
}
function historyPeriod(days) {
  historyTo.value = days ? shanghaiToday() : ''
  const start = new Date(`${shanghaiToday()}T12:00:00+08:00`)
  start.setDate(start.getDate() - Math.max(0, days - 1))
  historyFrom.value = days ? start.toISOString().slice(0, 10) : ''
  void loadHistory(0)
}
function historyTime(value) {
  if (!value) return '时间未记录'
  const stamp = /(Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`
  return new Date(stamp).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
}

function shanghaiToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
}
function formatElapsed(value = 0) {
  const seconds = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(seconds / 60)
  return minutes ? `${minutes} 分 ${String(seconds % 60).padStart(2, '0')} 秒` : `${seconds} 秒`
}
function formatTime(value) {
  if (!value) return '—'
  try { return new Date(value).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) } catch { return '—' }
}
function isPageVisible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}
function scheduleBatchPoll() {
  if (batchTimer) window.clearTimeout(batchTimer)
  batchTimer = null
  if (disposed || sessionExpired.value || !isPageVisible() || (!shouldPollBatch.value && !policy.value?.enabled)) return
  const normalDelay = !shouldPollBatch.value ? 30000 : overview.value?.batch_status === 'ready'
    ? 15000
    : Number(overview.value?.elapsed_seconds || 0) < 120 ? 3000 : 10000
  const delay = overviewPollFailures
    ? Math.min(60000, 10000 * (2 ** Math.min(overviewPollFailures, 3)))
    : normalDelay
  batchTimer = window.setTimeout(refreshBatchOverview, delay)
}
async function loadOverview({ silent = false } = {}) {
  if (disposed || sessionExpired.value || !project.value?.id || !tenantId.value) return
  const ticket = overviewReads.begin(overviewScope())
  if (!silent) refreshing.value = true
  try {
    const result = await projectWorkspaceApi.dailyTaskOverview(tenantId.value, project.value.id, historyDate.value, historyBatch.value)
    if (!disposed && !sessionExpired.value && overviewReads.accepts(ticket, overviewScope())) {
      overview.value = result
      overviewPollFailures = 0
      reportPreparationDiagnostics(result)
    }
  } catch (error) {
    if (!disposed && overviewReads.accepts(ticket, overviewScope())) throw error
  } finally {
    if (!silent && overviewReads.accepts(ticket, overviewScope())) refreshing.value = false
  }
}
async function refreshBatchOverview() {
  if (sessionExpired.value || !isPageVisible()) return
  if (refreshBatchOverview.needsInitialRetry) { await load(); return }
  try {
    await loadOverview({ silent: true })
    if (expandedPersonaId.value) {
      await loadPersonaTasks(expandedPersonaId.value, taskPage.value, { silent: true })
    }
    if (Date.now() - lastPolicyPollAt >= 30000) {
      lastPolicyPollAt = Date.now()
      const ticket = policyReads.begin(overviewScope())
      const result = await projectWorkspaceApi.dailyDispatchPolicy(tenantId.value, project.value.id)
      if (!disposed && !sessionExpired.value && policyReads.accepts(ticket, overviewScope())) policy.value = result
    }
    if (errorMessage.value === refreshBatchOverview.lastError) errorMessage.value = ''
  } catch (error) {
    overviewPollFailures += 1
    errorMessage.value = error?.message || '任务状态暂时无法更新，页面保留最后一次成功结果。'
    refreshBatchOverview.lastError = errorMessage.value
  }
  scheduleBatchPoll()
}

function handleVisibilityChange() {
  if (sessionExpired.value || !isPageVisible()) {
    if (batchTimer) window.clearTimeout(batchTimer)
    batchTimer = null
    return
  }
  void refreshBatchOverview()
}
function handleSessionExpired() {
  sessionExpired.value = true
  policyReads.invalidate()
  overviewReads.invalidate()
  pageReads.invalidate()
  personaReads.invalidate()
  historyReads.invalidate()
  historyResult.value = { items: [], total: 0 }
  if (batchTimer) window.clearTimeout(batchTimer)
  batchTimer = null
  overview.value = null
  expandedPersonaId.value = ''
  personaTasks.value = []
  stateMessage.value = ''
  loading.value = false
  refreshing.value = false
  errorMessage.value = '网页登录已过期，请重新登录后再查看最新任务进度。手机已经完成的任务和图片不会因此被删除。'
}
async function load() {
  if (disposed || sessionExpired.value || !project.value?.id || !tenantId.value) return
  const ticket = pageReads.begin(overviewScope())
  loading.value = !overview.value
  refreshing.value = Boolean(overview.value)
  errorMessage.value = ''
  const results = await Promise.allSettled([
    loadOverview({ silent: true }),
    projectWorkspaceApi.personaAssignments(tenantId.value, project.value.id),
    projectWorkspaceApi.dailyDispatchPolicy(tenantId.value, project.value.id),
    loadHistory(),
  ])
  if (disposed || !pageReads.accepts(ticket, overviewScope())) return
  if (results[1].status === 'fulfilled') assignments.value = Array.isArray(results[1].value) ? results[1].value : []
  if (results[2].status === 'fulfilled') {
    policyReads.invalidate()
    lastPolicyPollAt = Date.now()
    policy.value = results[2].value
    autoDispatchTime.value = results[2].value?.dispatch_time || '09:00'
  }
  const failed = results.find((item) => item.status === 'rejected')
  refreshBatchOverview.needsInitialRetry = Boolean(failed)
  if (failed) {
    errorMessage.value = failed.reason?.message || '部分云端状态暂时无法读取，页面已保留最后一次成功结果。'
    refreshBatchOverview.lastError = errorMessage.value
  }
  loading.value = false
  refreshing.value = false
  scheduleBatchPoll()
}
async function loadPersonaTasks(personaId, page = 1, { silent = false } = {}) {
  if (disposed || !personaId || personaId !== expandedPersonaId.value) return
  const ticket = personaReads.begin(personaScope())
  if (!silent) personaTasks.value = []
  const nextPage = Math.max(1, page)
  let rows
  try { rows = await projectWorkspaceApi.dailyTasks(tenantId.value, project.value.id, historyDate.value, {
    personaId,
    dispatchBatchId: historyBatch.value || overview.value?.dispatch_batch_id,
    limit: taskPageSize,
    offset: (nextPage - 1) * taskPageSize,
  }) } catch (error) {
    if (disposed || !personaReads.accepts(ticket, personaScope())) return
    throw error
  }
  if (disposed || !personaReads.accepts(ticket, personaScope())) return
  personaTasks.value = Array.isArray(rows) ? rows : []
  taskPage.value = nextPage
}
async function togglePersona(group) {
  if (expandedPersonaId.value === group.persona_id) {
    personaReads.invalidate()
    expandedPersonaId.value = ''
    personaTasks.value = []
    return
  }
  expandedPersonaId.value = group.persona_id
  try { await loadPersonaTasks(group.persona_id, 1) }
  catch (error) { errorMessage.value = error?.message || '手机任务明细暂时无法读取。' }
}
function taskTopic(task) { return task.latest_app_content?.content?.title || task.contract?.daily_task?.topic || task.contract?.brief?.topic || '今日内容任务' }
function taskStatusLabel(task) {
  const dimensions = task.status_dimensions || {}
  if (dimensions.delivery_state === 'succeeded') return '下发完成 · 图文已在手机生成'
  if (dimensions.control_state === 'cancelled_by_app') return '用户已终止'
  if (dimensions.control_state === 'stopped_by_manager') return '项目经理已停止'
  if (dimensions.completion_state === 'completed_awaiting_verification') return '任务已完成，等待 AI 项目经理核验'
  if (dimensions.generation_state === 'generated') return '图片已生成 · 等待完成回执'
  const recovery = phoneRecoveryLabel(task)
  if (recovery) return recovery
  if (task.generation_progress?.stage === 'failed' && isReceiptSyncFailure(task)) return '图片已生成，等待同步完成记录'
  if (task.generation_progress?.stage === 'failed') return '图片生成失败'
  const stage = task.generation_progress?.stage
  if (stage === 'copy_ready') return '文案已准备，等待手机生成图片'
  if (stage === 'retry_pending') return '等待手机重试失败步骤'
  if (stage === 'rendering') return `正在生成第 ${Math.min(3, (task.generation_progress?.ready_pages || 0) + 1)}/3 张图片`
  if (stage === 'page_ready') return `已生成 ${task.generation_progress?.ready_pages || 0}/3 张图片`
  if (stage === 'completed') return '3 张图片已生成 · 核对完成回执'
  if (task.app_delivery?.status === 'received') return '手机已收到，尚未收到生成确认'
  if (task.status === 'pending' && task.copy_preparation?.ready) return '等待手机领取'
  if (task.status === 'pending') return '等待人设文案'
  return task.status || '等待处理'
}
function taskReviewLabel(task) {
  const dimensions = task.status_dimensions || {}
  if (dimensions.publication_state === 'passed') return 'AI 项目经理已核验通过'
  if (dimensions.publication_state === 'rejected') return '有链接未通过，请用户重新提交'
  if (dimensions.publication_state === 'manual_review_required') return '链接需要项目经理人工复核'
  if (dimensions.publication_state === 'submitted') return `已提交链接，待核验${dimensions.missing_platforms?.length ? ` · 还缺 ${dimensions.missing_platforms.join('、')}` : ''}`
  if (task.review_status === 'approved') return '用户已确认'
  if (task.review_status === 'rejected' || task.summary_status === 'rejected') return '用户已退回'
  if (dimensions.delivery_state === 'succeeded') return '下发已完成 · 发布记录另行统计'
  return '尚未收到图片生成确认'
}
function platformLabel(value) {
  return ({ xiaohongshu: '小红书', douyin: '抖音', tiktok: 'TikTok', instagram: 'Instagram', threads: 'Threads', facebook: 'Facebook', wechat_channels: '视频号', xiaolvshu: '小绿书' })[value] || value
}
function proofStatusLabel(value) {
  return ({ submitted: '待审核', checking: '检查中', passed: '已通过', rejected: '未通过', manual_review_required: '需人工复核' })[value] || value
}
async function decidePublicationProof(task, proof, decision) {
  if (reviewingProofId.value) return
  const passed = decision === 'passed'
  const reason = passed
    ? 'AI 营销项目经理已打开链接并确认贴文真实存在。'
    : window.prompt('请填写未通过原因，用户会看到这段说明。', '链接无法打开，或内容与本任务不一致。')
  if (!reason) return
  if (!window.confirm(`${passed ? '确认通过' : '确认退回'} ${platformLabel(proof.platform)} 这条链接吗？`)) return
  reviewingProofId.value = proof.proof_id
  errorMessage.value = ''
  try {
    await projectWorkspaceApi.decidePublicationProof(
      tenantId.value,
      project.value.id,
      task.id,
      proof.proof_id,
      { expected_revision: Number(proof.revision), decision, reason },
    )
    stateMessage.value = passed ? '该平台链接已核验通过。' : '该平台链接已退回，等待用户重新提交。'
    await Promise.all([loadOverview({ silent: true }), loadPersonaTasks(expandedPersonaId.value, taskPage.value, { silent: true })])
  } catch (error) {
    errorMessage.value = error?.message || '链接审核结果未保存。'
  } finally {
    reviewingProofId.value = ''
  }
}
function appDeliveryLabel(task) {
  const delivery = task?.app_delivery || {}
  const version = delivery.app_version ? `App ${delivery.app_version}` : 'App 版本未知'
  return `${delivery.status === 'received' ? '手机已收到' : '等待手机领取'} · ${version}`
}
function preparationSteps(task) {
  const status = task?.preparation_status && typeof task.preparation_status === 'object'
    ? task.preparation_status
    : {}
  return [
    ['model_package', '人设文案包'], ['materials', '真实素材'], ['mobile_png', '手机 PNG'],
    ['gallery', '用户相册'], ['clipboard', '用户复制文案'], ['cloud_receipt', '生成完成回执'],
  ].map(([key, label]) => ({ key, label, ...(status[key] || { status: 'unavailable' }) }))
}
function preparationStatusLabel(step) {
  if (step.key === 'mobile_png' && !step.ready_count) return '尚未收到生成确认'
  if (step.status === 'disabled') return '等待开启自动保存'
  if (step.status === 'permission_required') return '等待相册授权'
  const count = step.ready_count ?? step.saved_count
  const total = step.required_count
  if (Number.isFinite(count) && Number.isFinite(total)) return `${count}/${total}`
  return ({ ready: '已准备', saved: '已保存', copied: '已复制', synced: '已同步', partial: '部分完成', disabled: '等待开启自动保存', permission_required: '等待相册授权', failed: '失败', preparing: '准备中', waiting: '等待中', unavailable: '状态待刷新' })[step.status] || step.status
}
function resetDispatchPlan() {
  dispatchPlan.value = null
  dispatchScopeSnapshot.value = null
}
function contentLineage(task) {
  const lineage = task?.copy_preparation?.content_lineage
  return lineage && Array.isArray(lineage.pages) ? lineage : null
}
function lineagePageLabel(page) {
  return `图 ${Number(page?.page_number || 0)}`
}
function materialPurposeLabel(purpose) {
  return { visible_detail: '可见细节', usage_scene: '使用场景', product_display: '产品展示' }[purpose] || '待说明用途'
}
function materialRelationLabel(status) {
  return { supported: '模型认为可承接正文', uncertain: '关系待确认', unrelated: '与正文无关', conflicting: '存在矛盾' }[status] || '尚未判定'
}
function contentReview(task) {
  return task?.content_review && typeof task.content_review === 'object' ? task.content_review : {}
}
function isContentPageLocked(task, pageNumber) {
  return (contentReview(task).locked_page_numbers || []).includes(Number(pageNumber))
}
function latestAppContent(task) {
  return task?.latest_app_content?.content && typeof task.latest_app_content.content === 'object'
    ? task.latest_app_content
    : null
}
async function handleContentReview(task, action, pageNumbers = [], patch = {}) {
  const taskId = String(task?.id || '')
  const revision = Number(task?.revision || task?.contract?.revision || 0)
  if (!taskId || revision < 1 || reviewingContentTaskId.value) return
  const currentReview = contentReview(task)
  let reason = String(patch.reason || '').trim()
  if (!reason) {
    const defaults = {
      rewrite_pages: '图片文字与正文重点不够一致，请严格按来源事实重写这一页。',
      request_material_details: '图片与当前卖点关系不足，请补充图片中的真实场景、产品和可证明信息。',
      update_locks: '项目经理确认锁定状态，避免后续调整静默覆盖。',
    }
    reason = window.prompt('请填写处理原因；用户和审计记录都会保留这段说明。', defaults[action] || '') || ''
  }
  if (reason.trim().length < 4) return
  if (action === 'rewrite_pages' && !window.confirm(`只重写第 ${pageNumbers.join('、')} 张图片文字吗？正文、素材、布局和其他图片不会改变。`)) return
  reviewingContentTaskId.value = `${taskId}:${action}:${pageNumbers.join('-')}`
  errorMessage.value = ''
  try {
    const idempotencyKey = `saas-content-review-v1-${taskId}-${revision}-${action}-${Date.now()}`
    const receipt = await projectWorkspaceApi.reviewDailyTaskContent(tenantId.value, project.value.id, taskId, {
      action,
      page_numbers: pageNumbers,
      locked_page_numbers: patch.lockedPageNumbers ?? (currentReview.locked_page_numbers || []),
      lock_copy: patch.lockCopy ?? Boolean(currentReview.lock_copy),
      reason,
      expected_revision: revision,
      idempotency_key: idempotencyKey,
    }, idempotencyKey)
    stateMessage.value = receipt?.assistant_message || 'AI 项目经理已保存处理结果。'
    await loadPersonaTasks(expandedPersonaId.value, taskPage.value, { silent: true })
  } catch (error) {
    errorMessage.value = error?.message || '图文处理结果没有保存。'
  } finally {
    reviewingContentTaskId.value = ''
  }
}
function toggleContentPageLock(task, pageNumber) {
  const locked = new Set(contentReview(task).locked_page_numbers || [])
  if (locked.has(pageNumber)) locked.delete(pageNumber)
  else locked.add(pageNumber)
  return handleContentReview(task, 'update_locks', [], { lockedPageNumbers: [...locked].sort() })
}
function toggleCopyLock(task) {
  return handleContentReview(task, 'update_locks', [], { lockCopy: !Boolean(contentReview(task).lock_copy) })
}
function dispatchScope() {
  return buildExactDispatchScope({
    assignments: activeAssignments.value,
    selectedIds: selectedAssignmentIds.value,
    localDate: shanghaiToday(),
    preferredPlatforms: policy.value?.preferred_platforms?.length ? policy.value.preferred_platforms : ['xiaohongshu'],
  })
}
async function openDispatchSelection() {
  if (dispatching.value) return
  dispatching.value = true
  errorMessage.value = ''
  try {
    const rows = await materialAssetsApi.list(tenantId.value, project.value.id, { statuses: 'ready' })
    materialChoices.value = rows.filter(item => item.mime_type?.startsWith('image/'))
    const available = new Set(materialChoices.value.map(item => item.id))
    selectedMaterialIds.value = selectedMaterialIds.value.filter(id => available.has(id))
    if (materialChoices.value.length === 2) selectedMaterialIds.value = materialChoices.value.map(item => item.id)
  } catch (error) {
    errorMessage.value = error?.message || '图片列表暂时无法读取，请重试。'
    return
  } finally { dispatching.value = false }
  selectedAssignmentIds.value = activeAssignments.value.map((item) => String(item.id))
  resetDispatchPlan()
  dispatchSelectionOpen.value = true
  stateMessage.value = '请确认本次由 AI 项目经理安排的手机助手；未确认前不会建立任务。'
}
function toggleMaterial(id) {
  if (selectedMaterialIds.value.includes(id)) selectedMaterialIds.value = selectedMaterialIds.value.filter(item => item !== id)
  else if (selectedMaterialIds.value.length < 2) selectedMaterialIds.value.push(id)
  resetDispatchPlan()
}
function closeDispatchSelection() {
  if (dispatching.value) return
  dispatchSelectionOpen.value = false
  selectedAssignmentIds.value = []
  resetDispatchPlan()
}
function setDispatchTargets(mode) {
  selectedAssignmentIds.value = mode === 'online'
    ? onlineAssignments.value.map((item) => String(item.id))
    : mode === 'all'
      ? activeAssignments.value.map((item) => String(item.id))
      : []
  resetDispatchPlan()
}
function toggleDispatchTarget(assignmentId) {
  const id = String(assignmentId || '')
  selectedAssignmentIds.value = selectedAssignmentIds.value.includes(id)
    ? selectedAssignmentIds.value.filter((item) => item !== id)
    : [...selectedAssignmentIds.value, id]
  resetDispatchPlan()
}
function assignmentTitle(assignment) {
  return [assignment.user_name || `未填写姓名（${assignment.app_code || String(assignment.id || '').slice(0, 8)}）`, assignment.masked_phone].filter(Boolean).join(' · ')
}
function assignmentFor(id) {
  return assignments.value.find((item) => String(item.id) === String(id)) || { id }
}
function assignmentSharing(assignment) {
  const profile = assignment.share_profile || {}
  return `地区：${profile.region || '未填写'} · 门店／网店：${profile.associated_name || '未填写'} · 常用话题：${profile.tags?.join(' ') || '未填写'}`
}
function assignmentStatus(assignment) {
  const version = assignment.app_version ? `App ${assignment.app_version}` : 'App 版本未知'
  return `${isRecentlyOnline(assignment) ? '在线' : '离线或超过 90 秒未连接'} · ${version}`
}
function dispatchContent(configuration = policy.value) {
  const source = configuration || {}
  return Object.fromEntries(['objective', 'content_brief', 'playbook', 'template', 'copy_requirements', 'variant', 'material_ids', 'report_digest', 'image_analysis', 'material_analysis_bindings']
    .map((key) => [key, source[key]]).filter(([, value]) => value !== undefined && value !== null))
}
async function prepareDispatchPlan() {
  const tid = tenantId.value, pid = project.value.id, viewScope = overviewScope()
  const current = () => !disposed && viewScope === overviewScope()
  const requireCurrent = () => { if (!current()) throw new Error('当前项目或批次已切换，请重新预览。') }
  stateMessage.value = '正在读取本次任务配置…'
  const savedPolicy = await projectWorkspaceApi.dailyDispatchPolicy(tid, pid)
  requireCurrent()
  if (savedPolicy) policy.value = savedPolicy
  if (!activeAssignments.value.length) throw new Error('当前没有已绑定人设的手机助手。')
  if (selectedMaterialIds.value.length !== 2) throw new Error('请为本次任务选择两张不同的可用图片。')
  stateMessage.value = '正在核对两张真实素材…'
  const prepared = await projectWorkspaceApi.prepareDailyDispatch(tid, pid, [...selectedMaterialIds.value])
  requireCurrent()
  if (!prepared?.configured || !prepared?.configuration) throw new Error('本次内容尚未准备完成，请重试。')
  const scope = dispatchScope()
  if (!scope.targets.length) throw new Error('请至少选择一台手机助手。')
  stateMessage.value = '正在核对所选手机的人设与接收资格…'
  const preflight = await projectWorkspaceApi.preflightDailyDispatch(tid, pid, { ...scope, material_ids: prepared.configuration.material_ids || [] })
  requireCurrent()
  materialPreflight.value = preflight
  if (!preflight?.ready) throw new Error(preflight?.assistant_message || '真实素材尚未通过云端校验。')
  const plan = await projectWorkspaceApi.planDailyDispatch(tid, pid, scope)
  requireCurrent()
  if (!plan?.selected_count) throw new Error('当前没有可接收任务的手机助手。')
  const plannedIds = new Set((plan.items || []).map((item) => String(item.app_binding_id || '')))
  if (Number(plan.selected_count) !== scope.targets.length || scope.targets.some((item) => !plannedIds.has(item.app_binding_id))) {
    throw new Error('云端冻结的手机清单与本次选择不一致，任务没有下发。')
  }
  dispatchPlan.value = { ...plan, configuration: prepared.configuration }
  dispatchScopeSnapshot.value = scope
  stateMessage.value = `AI 项目经理已冻结 ${plan.selected_count} 台手机；请核对后确认下发。`
}
async function dispatchToday() {
  openDispatchSelection()
}
async function previewDispatch() {
  if (dispatching.value) return
  const viewScope = overviewScope()
  dispatching.value = true
  errorMessage.value = ''
  stateMessage.value = '正在核对真实素材、人设和已绑定手机…'
  try {
    await prepareDispatchPlan()
  } catch (error) {
    if (disposed || viewScope !== overviewScope()) return
    resetDispatchPlan()
    stateMessage.value = ''
    if (isRecoverableDispatchConflict(error)) await load()
    errorMessage.value = error?.message || '当前手机清单无法冻结，请重新核对。'
  } finally { if (!disposed && viewScope === overviewScope()) dispatching.value = false }
}
async function confirmDispatch() {
  if (dispatching.value || !dispatchPlan.value?.plan_token || !dispatchScopeSnapshot.value) return
  const viewScope = overviewScope()
  dispatching.value = true
  errorMessage.value = ''
  stateMessage.value = `正在下发给已确认的 ${dispatchPlan.value.selected_count} 台手机…`
  try {
    const scope = dispatchScopeSnapshot.value
    const idempotencyKey = `saas-manual-${project.value.id}-${dispatchPlan.value.dispatch_plan_id}`
    const receipt = await projectWorkspaceApi.confirmDailyDispatch(tenantId.value, project.value.id, {
      ...scope, plan_token: dispatchPlan.value.plan_token, idempotency_key: idempotencyKey, ...dispatchContent(dispatchPlan.value.configuration),
    }, idempotencyKey)
    if (disposed || viewScope !== overviewScope()) return
    resetDispatchPlan()
    dispatchSelectionOpen.value = false
    selectedAssignmentIds.value = []
    stateMessage.value = receipt?.assistant_message || `已创建 ${receipt?.created_count || 0} 个手机任务，AI 项目经理正在准备人设文案。`
    await load()
  } catch (error) {
    if (disposed || viewScope !== overviewScope()) return
    resetDispatchPlan()
    if (isRecoverableDispatchConflict(error)) {
      await load()
      stateMessage.value = '项目状态已变化，请重新核对手机清单后再确认。'
    }
    errorMessage.value = error?.message || '今天的任务尚未下发。'
  } finally { if (!disposed && viewScope === overviewScope()) dispatching.value = false }
}
async function retryFailedBatch() {
  const batchId = String(overview.value?.dispatch_batch_id || '')
  if (retryingBatch.value || !batchId || !canRetryCloudPreparation(overview.value)) return
  retryingBatch.value = true
  errorMessage.value = ''
  stateMessage.value = 'AI 项目经理正在只重试失败文案，原任务和素材保持不变…'
  try {
    const idempotencyKey = `saas-copy-batch-retry-v1-${batchId}-${Date.now()}`
    const receipt = await projectWorkspaceApi.retryFailedDailyTaskBatch(
      tenantId.value, project.value.id, batchId, idempotencyKey,
    )
    stateMessage.value = receipt?.assistant_message || '失败文案已进入重试队列。'
    await load()
  } catch (error) { errorMessage.value = error?.message || '失败批次没有成功进入重试队列。' }
  finally { retryingBatch.value = false }
}
async function stopCurrentBatch() {
  const batchId = String(overview.value?.dispatch_batch_id || '')
  if (!canStopBatch.value || stoppingBatch.value || !batchId) return
  if (!window.confirm('只停止当前批次尚未完成的任务和未发送通知；已生成图片、已提交链接和已完成记录都会保留。确认停止吗？')) return
  stoppingBatch.value = true
  errorMessage.value = ''
  stateMessage.value = 'AI 项目经理正在停止当前批次的未完成任务…'
  try {
    const idempotencyKey = `saas-stop-v1-${batchId}`.slice(0, 128)
    const receipt = await projectWorkspaceApi.stopDailyTaskBatch(
      tenantId.value,
      project.value.id,
      batchId,
      {
        expected_revision: Number(overview.value?.batch_control?.revision || 0),
        reason: 'AI 营销项目经理从每日任务中心停止当前批次',
        idempotency_key: idempotencyKey,
      },
      idempotencyKey,
    )
    stateMessage.value = receipt?.assistant_message || '当前批次未完成任务已停止，已完成记录保持不变。'
    await load()
  } catch (error) {
    errorMessage.value = error?.message || '当前批次尚未停止，请刷新状态后重试。'
  } finally { stoppingBatch.value = false }
}
async function saveAutoDispatch(nextEnabled = Boolean(policy.value?.enabled)) {
  if (!policy.value?.configured || policySaving.value) return
  const changingEnabled = nextEnabled !== Boolean(policy.value.enabled)
  if (changingEnabled && !window.confirm(nextEnabled
    ? `确认每天 ${autoDispatchTime.value}（${policy.value.timezone}）自动下发任务吗？`
    : '确认关闭自动下发吗？关闭后不会再在每天 09:00 自动建立任务。')) return
  policySaving.value = true
  errorMessage.value = ''
  const viewScope = overviewScope()
  try {
    const source = policy.value
    policyReads.invalidate()
    const receipt = await projectWorkspaceApi.updateDailyDispatchPolicy(tenantId.value, project.value.id, {
      expected_revision: Number(source.revision || 0),
      enabled: nextEnabled,
      timezone: source.timezone || 'Asia/Shanghai',
      dispatch_time: autoDispatchTime.value || '09:00',
      max_tasks: Number(source.max_tasks || 1000),
      preferred_platforms: source.preferred_platforms || ['xiaohongshu'],
      objective: source.objective || {},
      content_brief: source.content_brief || {},
      playbook: source.playbook || {},
      template: source.template || {},
      copy_requirements: source.copy_requirements || {},
      variant: source.variant || {},
      material_ids: source.material_ids || [],
      report_digest: source.report_digest || null,
      image_analysis: source.image_analysis || null,
      material_analysis_bindings: source.material_analysis_bindings || null,
    })
    if (disposed || viewScope !== overviewScope()) return
    if (typeof receipt?.enabled !== 'boolean' || !receipt?.revision) throw new Error('保存回执尚未确认，请重新读取日程。')
    policyReads.invalidate()
    policy.value = { ...source, ...receipt, configured: true }
    autoDispatchTime.value = receipt.dispatch_time
    stateMessage.value = nextEnabled
      ? `自动下发已开启：每天 ${autoDispatchTime.value}（${source.timezone || 'Asia/Shanghai'}）。`
      : '自动下发已关闭；手动下发新批次仍可正常使用。'
    scheduleBatchPoll()
  } catch (error) {
    if (disposed || viewScope !== overviewScope()) return
    errorMessage.value = error?.message || '自动下发设置尚未保存，请刷新后重试。'
  } finally { if (!disposed && viewScope === overviewScope()) policySaving.value = false }
}
function isReceiptSyncFailure(task) {
  return ['cloud_asset_upload', 'completion_receipt_sync'].includes(String(task?.issue?.stage || ''))
}
function issueSummary(task) {
  const issue = task?.issue || {}
  if (isReceiptSyncFailure(task)) return `${issue.message || ''} ${issue.action || ''}`.trim()
  return `${issue.error_code || 'GENERATION_FAILED'} · ${issue.error_category || 'unknown'} · ${issue.message || ''} ${issue.action || ''}`.trim()
}
watch(() => [tenantId.value, project.value?.id], () => {
  policySaving.value = false
  dispatching.value = false
  refreshBatchOverview.needsInitialRetry = false
  resetDispatchPlan()
  dispatchSelectionOpen.value = false
  policyReads.invalidate()
  lastPolicyPollAt = 0
  overviewReads.invalidate()
  pageReads.invalidate()
  personaReads.invalidate()
  if (batchTimer) window.clearTimeout(batchTimer)
  overviewPollFailures = 0
  overview.value = null
  assignments.value = []
  policy.value = null
  expandedPersonaId.value = ''
  personaTasks.value = []
  void load()
}, { flush: 'sync' })
onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pm:session-expired', handleSessionExpired)
  void load()
})
onBeforeUnmount(() => {
  disposed = true
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pm:session-expired', handleSessionExpired)
  overviewReads.invalidate()
  pageReads.invalidate()
  personaReads.invalidate()
  if (batchTimer) window.clearTimeout(batchTimer)
})
</script>

<template>
  <section class="tasks-page" aria-labelledby="tasks-title">
    <header class="tasks-header">
      <div><p class="eyebrow">DAILY ASSISTANT MISSIONS</p><h1 id="tasks-title">每日任务中心</h1><p>AI 项目经理按人设准备内容，手机助手领取后在本地生成图片；发布仍由用户确认。</p></div>
      <div class="task-actions">
        <button type="button" :disabled="loading || refreshing" @click="load">{{ refreshing ? '正在更新…' : '重新读取' }}</button>
        <button v-if="canRetryCloudPreparation(overview)" type="button" :disabled="retryingBatch || loading || refreshing" @click="retryFailedBatch">{{ retryingBatch ? '正在继续准备（新版规则）…' : '按新版规则继续准备' }}</button>
        <button v-if="canStopBatch" class="stop-action" type="button" :disabled="stoppingBatch || loading || refreshing" @click="stopCurrentBatch">{{ stoppingBatch ? '正在停止当前批次…' : '停止当前批次' }}</button>
        <button class="dispatch-action" type="button" :disabled="dispatching || loading" @click="dispatchToday">{{ dispatching ? '正在检查并下发…' : '下发新一批任务' }}</button>
      </div>
    </header>
    <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
    <p v-if="stateMessage" class="task-state" role="status">{{ stateMessage }}</p>
    <p v-if="policy?.enabled" class="task-state">已安排每天 {{ policy.dispatch_time || autoDispatchTime }}（{{ policy.timezone || 'Asia/Shanghai' }}）自动准备任务，实际执行状态见下方；手动下发不会取消这条安排。</p>

    <section v-if="dispatchSelectionOpen && !sessionExpired" class="dispatch-targets" aria-labelledby="dispatch-targets-title">
      <fieldset :disabled="dispatching">
        <legend>本次使用的图片（已选 {{ selectedMaterialIds.length }}/2 张）</legend>
        <p v-if="materialChoices.length < 2">需要两张可用图片，请到真实素材库查看或补充。</p>
        <label v-for="asset in materialChoices" :key="asset.id" style="display:inline-flex;gap:8px;margin:8px">
          <input type="checkbox" :checked="selectedMaterialIds.includes(asset.id)"
            :disabled="!selectedMaterialIds.includes(asset.id) && selectedMaterialIds.length >= 2"
            @change="toggleMaterial(asset.id)">{{ asset.name }}
        </label>
      </fieldset>
      <header>
        <div><span>AI 项目经理安排范围</span><h2 id="dispatch-targets-title">确认本次手机助手</h2><p>已选择 {{ selectedAssignments.length }}/{{ activeAssignments.length }} 台；确认前不会建立任务。</p></div>
        <div class="target-shortcuts"><button type="button" :disabled="dispatching" @click="setDispatchTargets('all')">选择全部有效手机</button><button type="button" :disabled="dispatching || !onlineAssignments.length" @click="setDispatchTargets('online')">只选在线手机</button><button type="button" :disabled="dispatching" @click="setDispatchTargets('none')">清除选择</button></div>
      </header>
      <div class="target-list">
        <label v-for="assignment in activeAssignments" :key="assignment.id" :class="['target-card', { selected: selectedAssignmentIds.includes(String(assignment.id)) }]">
          <input type="checkbox" :checked="selectedAssignmentIds.includes(String(assignment.id))" :disabled="dispatching" @change="toggleDispatchTarget(assignment.id)">
          <span><strong>{{ assignmentTitle(assignment) }}</strong><small>{{ assignmentSharing(assignment) }}</small><small>分配人设：{{ assignment.persona_name || `人设 ${String(assignment.persona_id || '').slice(0, 8)}` }}</small><small>{{ assignmentStatus(assignment) }}</small></span>
        </label>
      </div>
      <div v-if="dispatchPlan" class="dispatch-plan" role="status">
        <strong>云端已冻结 {{ dispatchPlan.selected_count }} 台，不会自动换成其他手机</strong>
        <span v-for="item in dispatchPlan.items || []" :key="item.app_binding_id">{{ assignmentTitle(assignmentFor(item.app_binding_id)) }} · {{ item.platform || '小红书' }}<small>{{ assignmentSharing(assignmentFor(item.app_binding_id)) }}</small></span>
      </div>
      <footer>
        <button type="button" :disabled="dispatching" @click="closeDispatchSelection">取消</button>
        <button v-if="!dispatchPlan" class="primary" type="button" :disabled="dispatching || !selectedAssignments.length" @click="previewDispatch">{{ dispatching ? '正在核对…' : `核对并预览 ${selectedAssignments.length} 台` }}</button>
        <button v-else class="primary" type="button" :disabled="dispatching" @click="confirmDispatch">{{ dispatching ? '正在下发…' : `确认下发给 ${dispatchPlan.selected_count} 台` }}</button>
      </footer>
    </section>

    <section v-if="!loading && policy?.configured" class="auto-dispatch-settings" aria-labelledby="auto-dispatch-title">
      <header><div><span>旧每日规则</span><h2 id="auto-dispatch-title">{{ policy.enabled ? (policy.blocker ? '已开启，待处理' : '已开启') : '已关闭' }}</h2><p>这是之前保存的每日规则。新增周期或预设日程可使用下方独立设置。</p></div><strong :class="policy.enabled ? 'enabled' : 'disabled'">{{ policy.enabled ? (policy.blocker ? '上次执行未完成' : '旧规则已启用') : '旧规则已关闭' }}</strong></header>
      <div class="auto-dispatch-controls"><label>每天执行时间<input v-model="autoDispatchTime" type="time" :disabled="policySaving"></label><span>时区：{{ policy.timezone || 'Asia/Shanghai' }}</span><button type="button" :disabled="policySaving" @click="saveAutoDispatch(Boolean(policy.enabled))">{{ policySaving ? '正在保存…' : '保存时间' }}</button><button type="button" :class="policy.enabled ? 'disable-auto' : 'enable-auto'" :disabled="policySaving" @click="saveAutoDispatch(!policy.enabled)">{{ policy.enabled ? '关闭自动下发' : '开启自动下发' }}</button></div>
    </section>

    <DailyDispatchRecovery v-if="!sessionExpired && tenantId && project?.id" :tenant-id="tenantId" :project-id="project.id" :policy="policy" @changed="load" />
    <DispatchBindingReadiness v-if="!sessionExpired && tenantId && project?.id" :tenant-id="tenantId" :project-id="project.id" @changed="load" />
    <PublicationRatesPanel v-if="!sessionExpired && tenantId && project?.id" :tenant-id="tenantId" :project-id="project.id" :initial-date="historyDate" />
    <DispatchSchedulesPanel v-if="!sessionExpired && tenantId && project" :key="`${tenantId}/${project.id}/periodic`"
      kind="periodic" :tenant-id="tenantId" :project="project" :assignments="activeAssignments" @saved="loadHistory(0)" @batch="selectHistory" />
    <DispatchSchedulesPanel v-if="!sessionExpired && tenantId && project" :key="`${tenantId}/${project.id}/once`"
      kind="once" :tenant-id="tenantId" :project="project" :assignments="activeAssignments" @saved="loadHistory(0)" @batch="selectHistory" />
    <DispatchOccurrenceHistory v-if="!sessionExpired && tenantId && project" :key="`${tenantId}/${project.id}/occurrences`"
      :tenant-id="tenantId" :project="project" @batch="selectHistory" />

    <section v-if="!loading && ((!policy?.configured && policy?.material_readiness?.ready_image_count < 2) || activeAssignments.length === 0)" class="dispatch-readiness">
      <strong>任务下发还差以下准备</strong>
      <p v-if="!policy?.configured">{{ policyBlockerMessage || '正在读取本次任务的准备情况。' }}</p>
      <router-link v-if="!policy?.configured && policy?.material_readiness?.ready_image_count < 2" to="/workspace/materials">查看素材</router-link>
      <p v-if="activeAssignments.length === 0">至少需要一台已绑定人设的手机助手。</p><router-link v-if="activeAssignments.length === 0" to="/workspace/personas">去查看人设二维码</router-link>
    </section>

    <section v-if="!sessionExpired" class="batch-overview" aria-labelledby="history-title">
      <h2 id="history-title">历史任务 · 自动与手动下发</h2>
      <div class="auto-dispatch-controls">
        <label>开始日期<input v-model="historyFrom" type="date" @change="loadHistory(0)"></label>
        <label>结束日期<input v-model="historyTo" type="date" @change="loadHistory(0)"></label>
        <label>下发方式<select v-model="historyMode" @change="loadHistory(0)"><option value="">全部</option><option value="automatic">自动</option><option value="manual">手动</option></select></label>
        <button type="button" @click="historyPeriod(1)">今天</button>
        <button type="button" @click="historyPeriod(7)">近 7 天</button>
        <button type="button" @click="historyPeriod(0)">全部日期</button>
      </div>
      <p v-if="historyBusy" role="status">正在读取任务记录…</p>
      <p v-if="historyError" role="alert">{{ historyError }} <button type="button" @click="loadHistory()">重新读取历史</button></p>
      <p v-else-if="!historyBusy && !historyResult.items.length">所选范围没有任务记录。</p>
      <div class="history-table">
        <table v-if="historyResult.items.length">
          <thead><tr><th>下发时间</th><th>方式</th><th>人数</th><th>已接收</th><th>已生成</th><th>任务状态</th><th>详情</th></tr></thead>
          <tbody><tr v-for="batch in historyResult.items" :key="`${batch.local_date}:${batch.dispatch_batch_id}`">
            <td>{{ historyTime(batch.created_at) }}</td><td>{{ { automatic: '自动', manual: '手动' }[batch.dispatch_mode] || '来源未记录' }}</td>
            <td>{{ batch.target_count }}</td><td>{{ batch.received_count }}/{{ batch.target_count }}</td><td>{{ batch.generated_count }}/{{ batch.target_count }}</td>
            <td>{{ dailyHistoryStage(batch) }}<small v-if="batch.prepared_count != null">内容已准备 {{ batch.prepared_count }}/{{ batch.target_count }} · 等待手机同步 {{ batch.waiting_for_phone_count }}</small></td>
            <td><button type="button" :aria-pressed="historyBatch === batch.dispatch_batch_id" @click="selectHistory(batch)">{{ historyBatch === batch.dispatch_batch_id ? '正在查看' : '查看详情' }}</button></td>
          </tr></tbody>
        </table>
      </div>
      <div class="auto-dispatch-controls">
        <button type="button" :disabled="historyBusy || historyOffset === 0" @click="loadHistory(Math.max(0, historyOffset - 20))">上一页记录</button>
        <span>共 {{ historyResult.total }} 批 · 第 {{ Math.floor(historyOffset / 20) + 1 }} 页</span>
        <button type="button" :disabled="historyBusy || historyOffset + 20 >= historyResult.total" @click="loadHistory(historyOffset + 20)">下一页记录</button>
      </div>
      <p>下方正在查看：{{ historyDate }} · {{ historyBatch ? `批次 ${historyBatch.slice(0, 12)}` : '当天最新批次' }}</p>
    </section>

    <section v-if="!loading && overview" :class="['batch-overview', `batch-${overview.batch_status}`]">
      <div class="batch-heading"><div><span>本批任务进度</span><h2>{{ batchPreparationHeadline(overview) }}</h2><p v-if="overview.completed_at">全部 {{ metrics.completed_tasks }} 条任务已确认有效三图回执。</p><p v-else>任务记录已创建 {{ overview.dispatch_result?.published_count || 0 }}/{{ overview.dispatch_result?.target_count || 0 }}；不代表内容已准备好或手机已收到。</p></div><div class="elapsed"><span>{{ overview.completed_at ? '任务完成耗时' : overview.stopped_at ? '自动处理已停止' : '创建后经过' }}</span><strong>{{ formatElapsed(overview.elapsed_seconds) }}</strong><small v-if="overview.completed_at">完成于 {{ formatTime(overview.completed_at) }}</small><small v-else-if="overview.stopped_at">停止于 {{ formatTime(overview.stopped_at) }}</small></div></div>
      <div class="batch-progress" aria-label="内容准备和手机接收进度">
        <article><div><span>内容准备</span><strong>{{ contentProgressLabel(batchContentProgress) }}</strong></div><div class="progress-track"><span :style="{ width: (contentProgressPercent(batchContentProgress) ?? 0) + '%' }"></span></div><small>{{ metrics.ready_personas || 0 }}/{{ metrics.total_personas || 0 }} 个人设已完成内容检查</small></article>
        <article><div><span>手机接收</span><strong>{{ metrics.received_tasks || 0 }}/{{ metrics.covered_apps || 0 }} · {{ metrics.covered_apps ? Math.round((metrics.received_tasks || 0) / metrics.covered_apps * 100) : 0 }}%</strong></div><div class="progress-track"><span :style="{ width: (metrics.covered_apps ? (metrics.received_tasks || 0) / metrics.covered_apps * 100 : 0) + '%' }"></span></div><small>每台手机独立接收、生成和回执，不等待其他人设。</small></article>
      </div>
      <div class="batch-metrics">
        <article><span>任务记录已创建</span><strong>{{ overview.dispatch_result?.published_count || 0 }}/{{ overview.dispatch_result?.target_count || 0 }}</strong></article><article><span>App 接收率</span><strong>{{ metrics.received_tasks || 0 }}/{{ metrics.covered_apps || 0 }}</strong></article><article><span>图文生成率</span><strong>{{ metrics.png_ready_tasks || 0 }}/{{ metrics.covered_apps || 0 }}</strong></article><article><span>任务下发完成率</span><strong>{{ metrics.delivered_tasks || 0 }}/{{ metrics.covered_apps || 0 }}</strong></article>
      </div><p>{{ overview.assistant_message }}</p><p>后续发布与核验通过率在“下发与账号发布记录”中单独记录，不影响本批任务成功。</p>
    </section>

    <section v-if="sessionExpired" class="tasks-empty"><strong>网页登录已过期</strong><p>请重新登录后查看最新任务进度；手机上已生成的内容不会受影响。</p></section>
    <div v-else-if="loading" class="tasks-empty">正在读取云端任务…</div>
    <section v-else-if="overview?.batch_status === 'empty'" class="tasks-empty"><strong>今天尚未下发任务</strong><p>条件准备完成后，点击“下发新一批任务”。</p></section>
    <section v-else class="persona-section">
      <header class="persona-toolbar"><div><h2>按人设查看</h2><p>先看异常和准备状态，需要时再展开手机明细。</p></div><div class="filters"><input v-model="searchText" type="search" placeholder="搜索人设" aria-label="搜索人设"><select v-model="statusFilter" aria-label="筛选状态"><option value="all">全部状态</option><option value="problem">只看异常</option><option value="generating">正在生成</option><option value="retry_wait">等待重试</option><option value="failed">准备失败</option><option value="ready">已准备</option></select></div></header>
      <div class="persona-list">
        <article v-for="group in filteredGroups" :key="group.persona_id" :class="['persona-row', `status-${group.copy_status || 'queued'}`, { 'status-recovering': isRecoveringPreparation(group) }]">
          <button class="persona-main" type="button" @click="togglePersona(group)"><span class="status-dot"></span><span class="persona-name"><strong>{{ group.display_name }}</strong><small>覆盖 {{ group.covered_apps }} 台手机</small></span><span class="persona-state"><strong>{{ group.status_label }}</strong><small>{{ group.stopped_at ? `自动处理停止于 ${formatTime(group.stopped_at)} · 总历时 ${formatElapsed(group.elapsed_seconds)}` : `${group.completed_at ? '准备耗时' : '准备已耗时'} ${formatElapsed(group.elapsed_seconds)}` }}</small></span><span class="persona-counts">已接收 {{ group.received_tasks }}/{{ group.covered_apps }}<small>待接收 {{ Math.max(0, group.covered_apps - group.received_tasks) }} · 图片 {{ group.png_ready_tasks }}/{{ group.covered_apps }}</small></span><span class="expand-icon">{{ expandedPersonaId === group.persona_id ? '收起' : '展开' }}</span></button>
          <p class="persona-note">{{ preparationTimingText(group) }}</p>
          <div v-if="group.content_progress" :class="['persona-progress', { 'persona-progress-stopped': group.content_progress.execution_state === 'needs_attention' }]">
            <div><span>内容准备</span><strong>{{ contentProgressLabel(group.content_progress) }}</strong><small>{{ contentProgressStageLabel(group.content_progress) }}</small></div>
            <div class="progress-track"><span :style="{ width: (contentProgressPercent(group.content_progress) ?? 0) + '%' }"></span></div>
            <div v-if="contentProgressFieldStatuses(group.content_progress).length" class="content-field-statuses" aria-label="内容部分检查结果">
              <span v-for="item in contentProgressFieldStatuses(group.content_progress)" :key="item.field" :class="item.status">{{ item.label }}：{{ item.statusLabel }}</span>
            </div>
          </div>
          <div v-if="group.error?.execution_state === 'waiting_retry'" class="persona-note">下次自动恢复：{{ formatTime(group.next_attempt_at) }}</div>
          <div v-else-if="group.error?.execution_state === 'waiting_worker'" class="persona-note">任务已保存，等待内容服务领取。</div>
          <div v-else-if="group.error?.execution_state === 'worker_stale'" class="persona-note">内容服务尚未报告新的处理进展，任务会在服务恢复后继续。</div>
          <div v-if="isRecoveringPreparation(group)" class="persona-recovery">
            <strong>{{ contentProgressStageLabel(group.content_progress || {}) }}</strong>
            <span>系统正在只处理当前内容部分；已通过内容和已接收手机均会保留。</span>
          </div>
          <div v-if="shouldPresentPreparationFailure(group)" :class="['persona-error', { 'persona-recovery': group.error.retryable }]">
            <strong>{{ group.error.title }}</strong><span>{{ group.error.message }}</span>
            <small v-if="group.error.progress">已保存 {{ group.error.progress.saved_unit_count }}/{{ group.error.progress.total_unit_count }} 个内容部分{{ group.error.progress.last_progress_at ? ` · 最近内容更新 ${formatTime(group.error.progress.last_progress_at)}` : '' }}</small>
            <details v-if="group.error.preparation_stage" class="preparation-diagnostic">
              <summary>{{ group.error.retryable ? '查看系统处理记录' : '查看失败定位' }}</summary>
              <p>{{ group.error.retryable ? '当前处理步骤：' : '失败步骤：' }}{{ group.error.preparation_stage_label || group.error.preparation_stage }}</p>
              <p v-if="group.error.failed_field">失败位置：{{ group.error.failed_field }}</p>
              <p v-if="group.error.failure_reason">检查原因：{{ group.error.failure_reason }}</p>
              <p>诊断编号：{{ group.error.diagnostic_id || '暂未生成' }}</p>
              <ol v-if="group.error.diagnostic_events?.filter((event) => event?.success === false).length">
                <li v-for="event in group.error.diagnostic_events.filter((event) => event?.success === false)" :key="event.sequence || event.phase || event.event">
                  {{ event.phase || '内容准备' }} · {{ event.detail || '结构检查未通过' }}
                </li>
              </ol>
              <p>{{ group.error.recovery_action === 'repair_rule_upgrade' ? '这不是自动重试。点击“按新版规则继续准备”后，系统只会重新检查原任务中仍未通过的部分。' : group.error.execution_state === 'working' ? '后台已确认正在处理；已完成内容会保留，无需重复下发。' : group.error.execution_state === 'waiting_retry' ? '本轮已暂停，将按计划继续；无需重复下发。' : group.error.execution_state === 'waiting_worker' ? '任务尚未被内容服务领取；已完成内容会保留，无需重复下发。' : group.error.execution_state === 'worker_stale' ? '内容服务尚未报告新的进展；任务会在服务恢复后继续。' : group.error.retryable ? '系统将按当前规则继续处理；无需重复下发。' : '自动修复已停止，已保留通过的内容；请按上方具体检查原因处理。' }}</p>
            </details>
            <template v-if="group.error.grounding || group.error.category === 'material_relevance'">
              <details v-if="group.error.grounding" class="grounding-evidence" open>
                <summary>查看本次实际卖点与图片依据</summary>
                <p>待处理图片：{{ group.error.grounding.failed_slot }} · {{ group.error.grounding.reason }}</p>
                <p v-if="group.error.grounding.persona_context?.daily_scene">人设场景：{{ group.error.grounding.persona_context.daily_scene }}</p>
                <p v-if="group.error.grounding.task_context?.selected_pain_point">本次用户顾虑：{{ group.error.grounding.task_context.selected_pain_point }}</p>
                <p v-if="group.error.grounding.task_context?.use_scenario">本次使用场景：{{ group.error.grounding.task_context.use_scenario }}</p>
                <p v-if="group.error.grounding.task_context?.solution">本次解决方式：{{ group.error.grounding.task_context.solution }}</p>
                <p v-if="group.error.grounding.task_context?.content_angle">本次分享角度：{{ group.error.grounding.task_context.content_angle }}</p>
                <p><b>本次选中的卖点（来自核验资料）：</b></p>
                <ol><li v-for="(usp, index) in group.error.grounding.selected_usps" :key="index">{{ usp }}</li></ol>
                <div class="grounding-materials">
                  <article v-for="material in group.error.grounding.materials" :key="material.material_slot">
                    <b>{{ material.material_slot }} · 素材 {{ material.material_id }}</b>
                    <p>本次图片说明：{{ material.description || '未填写' }}</p>
                    <template v-if="material.proposed_relation">
                      <p>本图关联卖点：{{ material.proposed_relation.source_fact || '引用的卖点不在输入资料中' }}</p>
                      <p>图片用途：{{ materialPurposeLabel(material.proposed_relation.purpose) }} · {{ materialRelationLabel(material.proposed_relation.relation_status) }}</p>
                      <p>AI 关联说明：{{ material.proposed_relation.explanation }}</p>
                      <p>引用正文：{{ material.proposed_relation.source_copy_excerpt }}（{{ material.proposed_relation.copy_quote_exists ? '原文存在' : '原文不存在，不能使用' }}）</p>
                      <p>引用图片说明：{{ material.proposed_relation.description_excerpt }}（{{ material.proposed_relation.description_quote_exists ? '原说明存在' : '原说明不存在，不能使用' }}）</p>
                      <p v-if="!material.proposed_relation.material_id_matches">模型引用的素材 ID 与本次素材不一致，不能使用。</p>
                      <small>这是待审的模型关系说明，不是功效或亲测证明；对照原图与原说明确认，不需要补关键词凑分。</small>
                    </template>
                    <template v-else>
                      <p v-for="(match, index) in material.matches" :key="index">历史卖点 {{ index + 1 }}：{{ match.usp }}；{{ match.matched_terms.length ? `历史词面线索：${match.matched_terms.join('、')}` : '历史规则未找到共同词，不代表图片无关' }}</p>
                    </template>
                  </article>
                </div>
                <details><summary>查看已生成正文（诊断快照，非可发布成品）</summary><b>{{ group.error.grounding.post.title }}</b><p class="grounding-post">{{ group.error.grounding.post.body }}</p><small>正文版本：{{ group.error.grounding.copy_revision }}</small></details>
              </details>
              <p v-else>历史失败包未保存所选卖点和正文快照，无法准确还原；不会用当前资料猜填历史结果。</p>
              <p>建议描述图片真实可见的主体、场景和细节，不要为通过检查编造功效。修改素材不会自动修改已冻结的旧任务。</p>
              <router-link v-if="project?.id" :to="`/project-manager/projects/${project.id}/materials`">去补充素材说明或换图</router-link>
            </template>
          </div>
          <div v-if="expandedPersonaId === group.persona_id" class="device-details">
            <div v-if="!personaTasks.length" class="device-empty">正在读取手机明细，或当前页没有记录。</div>
            <article v-for="task in personaTasks" :key="task.id" class="device-task">
              <div><strong>{{ taskTopic(task) }}</strong><span>{{ taskStatusLabel(task) }}</span></div>
              <p>{{ assignmentTitle(assignmentFor(task.assignment_id)) }} · {{ task.platform || '小红书' }} · 图片 {{ task.generation_progress?.ready_pages || 0 }}/3 · {{ taskReviewLabel(task) }}</p>
              <p>{{ assignmentSharing(assignmentFor(task.assignment_id)) }} · 分配人设：{{ group.display_name }}</p>
              <p class="app-delivery">{{ appDeliveryLabel(task) }}</p>
              <p>任务 {{ task.id }} · 第 {{ task.revision }} 版 · 发布文案：{{ task.copy_preparation?.ready ? '已准备' : '准备中' }}</p>
              <p>下发 {{ historyTime(task.task_timeline?.dispatched_at || task.created_at) }} · 接收 {{ historyTime(task.task_timeline?.received_at) }}<br>
                图片完成 {{ historyTime(task.task_timeline?.generated_at) }} · 云端确认 {{ historyTime(task.task_timeline?.confirmed_at) }}</p>
              <div class="preparation-status" aria-label="发布准备分层状态"><div v-for="step in preparationSteps(task)" :key="step.key" :class="['preparation-step', step.status]"><span>{{ step.label }}</span><strong>{{ preparationStatusLabel(step) }}</strong></div></div>
              <details v-if="contentLineage(task)" class="content-lineage">
                <summary>AI 项目经理图文依据</summary>
                <p class="lineage-order">先结合人设、痛点、USP 与图片说明完成正文 → 锁定原文及素材引用 → 提炼图片文字；两张图可承接同一 USP 的不同角度。</p>
                <div class="content-review-toolbar">
                  <span>{{ contentReview(task).assistant_message || '逐页检查图文关系；不相关时先补素材说明。' }}</span>
                  <button type="button" :disabled="Boolean(reviewingContentTaskId)" @click="toggleCopyLock(task)">{{ contentReview(task).lock_copy ? '解除正文锁定' : '锁定已确认正文' }}</button>
                </div>
                <div class="lineage-pages">
                  <article v-for="page in contentLineage(task).pages" :key="page.page_number">
                    <header><strong>{{ lineagePageLabel(page) }}</strong><span>{{ page.source_fact }}</span></header>
                    <p><b>素人分享理由：</b>{{ page.ordinary_person_reason }}</p>
                    <p><b>对应真实素材：</b>{{ page.image_description }}</p>
                    <p v-if="page.material_purpose"><b>图片用途：</b>{{ materialPurposeLabel(page.material_purpose) }}；{{ page.relation_explanation }}（不作为功效证明）</p>
                    <p><b>来源文案：</b>{{ page.source_copy_excerpt }}</p>
                    <footer class="content-review-actions">
                      <button type="button" :disabled="Boolean(reviewingContentTaskId) || isContentPageLocked(task, page.page_number)" @click="handleContentReview(task, 'rewrite_pages', [page.page_number])">只重写这张图片文字</button>
                      <button type="button" :disabled="Boolean(reviewingContentTaskId)" @click="handleContentReview(task, 'request_material_details', [page.page_number])">退回补充素材说明</button>
                      <button type="button" :disabled="Boolean(reviewingContentTaskId)" @click="toggleContentPageLock(task, page.page_number)">{{ isContentPageLocked(task, page.page_number) ? '解除本页锁定' : '锁定本页' }}</button>
                    </footer>
                  </article>
                </div>
              </details>
              <details v-if="latestAppContent(task)" class="latest-app-content">
                <summary>用户 App 最新修改版本</summary>
                <p>任务第 {{ latestAppContent(task).input_revision }} 版 · {{ formatTime(latestAppContent(task).received_at) }}</p>
                <strong>{{ latestAppContent(task).content.title || '未填写标题' }}</strong>
                <p>{{ latestAppContent(task).content.body || '未同步正文' }}</p>
                <p v-if="latestAppContent(task).content.tags?.length">{{ latestAppContent(task).content.tags.join(' ') }}</p>
                <small v-if="latestAppContent(task).review">确认记录：{{ latestAppContent(task).review.decision }} · {{ formatTime(latestAppContent(task).review.received_at) }}</small>
              </details>
              <section v-if="task.publication_proofs?.length" class="publication-proofs" aria-label="各平台发布链接与审核结果">
                <header><strong>本篇图文的平台链接</strong><span>任务完成与审核通过分开记录</span></header>
                <article v-for="proof in task.publication_proofs" :key="proof.proof_id" class="publication-proof">
                  <div><strong>{{ platformLabel(proof.platform) }}</strong><span :class="`proof-${proof.verification_status}`">{{ proofStatusLabel(proof.verification_status) }}</span></div>
                  <a :href="proof.submitted_url" target="_blank" rel="noopener noreferrer">{{ proof.submitted_url }}</a>
                  <PublicationDataPanel :proof="proof" />
                  <p v-if="proof.user_message">审核说明：{{ proof.user_message }}</p>
                  <footer v-if="!['passed', 'rejected'].includes(proof.verification_status)">
                    <button type="button" :disabled="Boolean(reviewingProofId)" @click="decidePublicationProof(task, proof, 'passed')">打开确认后通过</button>
                    <button class="reject-proof" type="button" :disabled="Boolean(reviewingProofId)" @click="decidePublicationProof(task, proof, 'rejected')">退回并说明原因</button>
                  </footer>
                </article>
              </section>
              <div v-if="task.issue" class="task-issue"><strong>{{ phoneIssueTitle(task) }}</strong><span>{{ issueSummary(task) }}</span><small v-if="task.issue.page_index">当前处理：图 {{ task.issue.page_index }} · App 已自动恢复 {{ task.issue.retry_count || 0 }} 次</small><small>{{ task.issue.retryable === false ? '任务和已有图片已保留，请按错误原因处理后恢复。' : '无需重新下发，用户 App 会继续同一任务直到成功。' }}</small></div>
            </article>
            <footer class="pagination"><button type="button" :disabled="taskPage <= 1" @click="loadPersonaTasks(group.persona_id, taskPage - 1)">上一页</button><span>第 {{ taskPage }} 页，每页 {{ taskPageSize }} 台</span><button type="button" :disabled="personaTasks.length < taskPageSize" @click="loadPersonaTasks(group.persona_id, taskPage + 1)">下一页</button></footer>
          </div>
        </article>
      </div><div v-if="!filteredGroups.length" class="tasks-empty">没有符合筛选条件的人设。</div>
    </section>
  </section>
</template>

<style scoped>
.tasks-page, .batch-overview { min-width: 0; }
.auto-dispatch-controls button { min-width: 80px; white-space: nowrap; }
.history-table + .auto-dispatch-controls { flex-wrap: wrap; }
.history-table button, .auto-dispatch-controls select { min-height: 40px; padding: 0 12px; border: 1px solid #bdd4d5; border-radius: 9px; background: #fff; color: #08746d; }
.history-table { overflow-x: auto; margin: 16px 0; }
.history-table table { width: 100%; border-collapse: collapse; text-align: left; }
.history-table th, .history-table td { padding: 10px; border-bottom: 1px solid #dce6ed; white-space: nowrap; }
.history-table button[aria-pressed="true"] { font-weight: 700; background: #e6f4f2; }
.grounding-evidence{min-width:0;padding:12px;background:#fff;border-radius:8px;color:#294b57;overflow-wrap:anywhere}.grounding-evidence summary{cursor:pointer;font-weight:800}.grounding-materials{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.grounding-materials article{padding:10px;background:#f3f7f8;border-radius:8px}.grounding-post{white-space:pre-wrap}.grounding-evidence p,.grounding-evidence li{line-height:1.6}.persona-error a{color:#08746d;font-weight:800}@media(max-width:700px){.grounding-materials{grid-template-columns:1fr}}
.tasks-page{display:grid;gap:20px;color:#203b43}.tasks-header{display:flex;justify-content:space-between;gap:24px;padding:28px;border:1px solid #dbe6ea;border-radius:18px;background:#fff}.eyebrow{margin:0 0 8px;color:#078279;font-size:12px;font-weight:900;letter-spacing:.14em}.tasks-header h1{margin:0;color:#17343b;font-size:34px}.tasks-header p:not(.eyebrow){max-width:760px;margin:10px 0 0;color:#6b8087;line-height:1.7}.task-actions{display:flex;align-items:flex-start;flex-wrap:wrap;gap:10px}.task-actions button,.pagination button{min-height:42px;padding:0 17px;border:1px solid #bad8d5;border-radius:10px;color:#08746d;background:#edf8f7;font-weight:800}.task-actions .dispatch-action{color:#fff;background:#0b8379}.task-actions button:disabled{cursor:not-allowed;opacity:.58}.task-state,.form-error{margin:0;padding:13px 16px;border-radius:10px}.task-state{color:#31565d;background:#eaf7f5}.form-error{color:#8f2118;background:#fff0ef}.dispatch-readiness{padding:18px;border:1px solid #ead39c;border-radius:14px;background:#fff8e8;color:#5e4d29}.dispatch-readiness p{margin:8px 0}.dispatch-readiness a{margin-right:18px;color:#08746d;font-weight:800}.batch-overview{padding:22px;border:1px solid #b8ddd8;border-radius:16px;background:#fff}.batch-overview.batch-slow{border-color:#e7ca76;background:#fffaf0}.batch-overview.batch-attention,.batch-overview.batch-failed{border-color:#efaaa3;background:#fff7f6}.batch-heading{display:flex;justify-content:space-between;gap:20px}.batch-heading span,.elapsed span{color:#71868d;font-size:12px}.batch-heading h2{margin:5px 0 0;font-size:22px}.elapsed{text-align:right}.elapsed strong{display:block;margin-top:5px;color:#17343b;font-size:20px}.progress-track{height:8px;margin:18px 0;border-radius:999px;overflow:hidden;background:#e6eeee}.progress-track span{display:block;height:100%;border-radius:inherit;background:#0b8379}.batch-metrics{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}.batch-metrics article{padding:13px;border-radius:11px;background:#f3f7f8}.batch-metrics span,.batch-metrics strong{display:block}.batch-metrics span{color:#768a91;font-size:12px}.batch-metrics strong{margin-top:6px;font-size:21px}.batch-overview>p{margin:14px 0 0;color:#62777e}.persona-section{padding:22px;border:1px solid #dbe6ea;border-radius:16px;background:#fff}.persona-toolbar{display:flex;justify-content:space-between;gap:20px;align-items:end}.persona-toolbar h2{margin:0;font-size:22px}.persona-toolbar p{margin:6px 0 0;color:#71868d}.filters{display:flex;gap:9px}.filters input,.filters select{min-height:40px;border:1px solid #cedcdf;border-radius:9px;padding:0 12px;background:#fff}.persona-list{display:grid;gap:10px;margin-top:18px}.persona-row{overflow:hidden;border:1px solid #dce6e9;border-radius:13px}.persona-main{display:grid;grid-template-columns:12px minmax(180px,1.5fr) minmax(120px,1fr) minmax(120px,1fr) auto;align-items:center;gap:14px;width:100%;padding:16px;border:0;background:#fff;text-align:left;color:inherit}.persona-main:hover{background:#f8fbfb}.status-dot{width:10px;height:10px;border-radius:50%;background:#9aabb0}.status-generating .status-dot,.status-queued .status-dot{background:#2d78cf}.status-retry_wait .status-dot{background:#d08b14}.status-failed .status-dot{background:#ca3932}.status-ready .status-dot{background:#1b9a5a}.persona-name,.persona-state,.persona-counts{display:grid;gap:4px}.persona-name small,.persona-state small,.persona-counts small{color:#75888e;font-size:12px}.persona-state>strong{color:#0b8379}.status-failed .persona-state>strong{color:#b42318}.status-retry_wait .persona-state>strong{color:#946200}.expand-icon{color:#08746d;font-weight:800}.persona-note,.persona-error{margin:0 16px 12px;padding:10px 12px;border-radius:9px}.persona-note{color:#71530f;background:#fff5d9}.persona-error{display:grid;gap:4px;color:#8f2118;background:#fff0ef}.persona-error span{font-size:13px}.device-details{display:grid;gap:10px;padding:14px;border-top:1px solid #e3ebed;background:#f7fafb}.device-task{padding:14px;border:1px solid #dce6e9;border-radius:10px;background:#fff}.device-task>div:first-child{display:flex;justify-content:space-between;gap:15px}.device-task>div:first-child span{color:#08746d;font-weight:800}.device-task p{margin:7px 0 0;color:#71868d;font-size:13px}.task-issue{display:grid;gap:5px;margin-top:10px;padding:10px;border-radius:8px;color:#714f13;background:#fff3d2}.retry-actions{display:flex;gap:8px;margin-top:5px}.retry-actions button{min-height:34px;border:1px solid #d4b86e;border-radius:8px;background:#fff;color:#714f13;font-weight:800}.pagination{display:flex;justify-content:center;align-items:center;gap:12px;padding-top:5px}.pagination span{color:#71868d;font-size:12px}.tasks-empty{padding:44px;border:1px solid #dce7ea;border-radius:15px;background:#fff;text-align:center}.tasks-empty p{color:#71868d}@media(max-width:900px){.batch-metrics{grid-template-columns:repeat(2,1fr)}.persona-main{grid-template-columns:12px 1fr auto}.persona-state,.persona-counts{grid-column:2}.expand-icon{grid-column:3;grid-row:1 / span 3}}@media(max-width:700px){.tasks-header,.persona-toolbar{display:grid}.task-actions,.filters{width:100%}.task-actions button{flex:1}.filters{display:grid}.batch-metrics{grid-template-columns:1fr 1fr}.persona-section{padding:15px}.persona-main{padding:14px}.device-task>div:first-child{display:grid}}
.preparation-status{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin-top:10px}.preparation-step{display:grid;gap:3px;padding:8px;border-radius:7px;background:#f2f6f7}.preparation-step span{color:#6d8188;font-size:11px}.preparation-step strong{font-size:12px}.preparation-step.ready,.preparation-step.saved,.preparation-step.copied,.preparation-step.synced{background:#e8f7ef;color:#187647}.preparation-step.failed{background:#fff0ef;color:#a32a22}.preparation-step.preparing,.preparation-step.waiting{background:#edf5ff;color:#2769b0}.preparation-step.unavailable{background:#fff7e5;color:#805b0b}.task-issue small{font-size:12px}.retry-actions{flex-wrap:wrap}@media(max-width:700px){.preparation-status{grid-template-columns:repeat(2,minmax(0,1fr))}}
.batch-overview.batch-attention{border-color:#e7ca76;background:#fffaf0}.batch-metrics{grid-template-columns:repeat(5,minmax(0,1fr))}.device-task .app-delivery{color:#31565d;font-weight:800}.status-completed .status-dot{background:#1b9a5a}@media(max-width:1100px){.batch-metrics{grid-template-columns:repeat(3,1fr)}.persona-main{grid-template-columns:12px 1fr auto}.persona-state,.persona-counts{grid-column:2}.expand-icon{grid-column:3;grid-row:1 / span 3}}@media(max-width:900px){.batch-metrics{grid-template-columns:repeat(2,1fr)}}
.dispatch-targets{display:grid;gap:16px;padding:22px;border:1px solid #9bcfc9;border-radius:16px;background:#f7fcfb}.dispatch-targets header{display:flex;justify-content:space-between;gap:20px}.dispatch-targets header span{color:#08746d;font-size:12px;font-weight:900}.dispatch-targets h2{margin:5px 0 0;color:#17343b}.dispatch-targets p{margin:7px 0 0;color:#62777e}.target-shortcuts,.dispatch-targets footer{display:flex;flex-wrap:wrap;gap:8px}.dispatch-targets button{min-height:38px;padding:0 14px;border:1px solid #bad8d5;border-radius:9px;color:#08746d;background:#fff;font-weight:800}.dispatch-targets button.primary{color:#fff;background:#0b8379}.dispatch-targets button:disabled{opacity:.55}.target-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.target-card{display:flex;gap:10px;align-items:flex-start;padding:13px;border:1px solid #d5e4e5;border-radius:10px;background:#fff}.target-card.selected{border-color:#45a99f;background:#edf9f7}.target-card input{margin-top:4px}.target-card span{display:grid;gap:3px}.target-card small{color:#71868d}.dispatch-plan{display:grid;gap:5px;padding:13px;border-radius:10px;color:#245f5a;background:#dff4f1}.dispatch-targets footer{justify-content:flex-end}@media(max-width:800px){.dispatch-targets header{display:grid}.target-list{grid-template-columns:1fr}.target-shortcuts button,.dispatch-targets footer button{flex:1}}
.task-actions .stop-action{border-color:#d99b92;color:#9b2c22;background:#fff3f1}.batch-overview.batch-stopped{border-color:#aebcc1;background:#f5f7f8}
.batch-progress{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin:18px 0}.batch-progress article,.persona-progress{display:grid;gap:8px;padding:13px;border-radius:11px;background:#edf5ff;color:#24558f}.batch-progress article>div:first-child{display:flex;justify-content:space-between;gap:10px;align-items:baseline}.batch-progress span,.batch-progress small,.persona-progress span,.persona-progress small{color:#5b7795;font-size:12px}.batch-progress strong,.persona-progress strong{font-size:15px}.batch-progress .progress-track,.persona-progress .progress-track{height:7px;margin:0;background:#dbe9f6}.batch-progress .progress-track span,.persona-progress .progress-track span{background:#2d78cf}.persona-progress{margin:0 16px 12px}.persona-progress>div:first-child{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 10px}.persona-progress-stopped{background:#fff7e9;color:#805b0b}.persona-progress-stopped span,.persona-progress-stopped small{color:#805b0b}.persona-progress-stopped .progress-track{background:#f4e5bd}.persona-progress-stopped .progress-track span{background:#ca8a12}.content-field-statuses{display:flex;flex-wrap:wrap;gap:6px}.content-field-statuses span{padding:3px 7px;border-radius:999px;background:#edf1f2;color:#66777c!important}.content-field-statuses .passed{background:#e8f7ef;color:#187647!important}.content-field-statuses .needs_repair{background:#fff3d2;color:#805b0b!important}.persona-recovery{display:grid;gap:4px;margin:0 16px 12px;padding:10px 12px;border-radius:9px;color:#24558f;background:#edf5ff}.persona-recovery span{font-size:13px}@media(max-width:700px){.batch-progress{grid-template-columns:1fr}.batch-progress article>div:first-child{display:grid}}
.status-recovering .status-dot{background:#2d78cf}.status-recovering .persona-state>strong{color:#2769b0}
.auto-dispatch-settings{display:grid;gap:14px;padding:20px;border:1px solid #d6e3e5;border-radius:15px;background:#fff}.auto-dispatch-settings header{display:flex;justify-content:space-between;gap:20px}.auto-dispatch-settings header span{color:#08746d;font-size:12px;font-weight:900}.auto-dispatch-settings h2{margin:4px 0}.auto-dispatch-settings header p{margin:5px 0 0;color:#687e85}.auto-dispatch-settings header>strong{align-self:start;padding:7px 11px;border-radius:999px}.auto-dispatch-settings header>strong.enabled{color:#176f49;background:#e4f6ed}.auto-dispatch-settings header>strong.disabled{color:#66777c;background:#edf1f2}.auto-dispatch-controls{display:flex;align-items:end;flex-wrap:wrap;gap:10px}.auto-dispatch-controls label{display:grid;gap:5px;color:#526970;font-size:12px}.auto-dispatch-controls input,.auto-dispatch-controls button{min-height:40px;border:1px solid #bdd4d5;border-radius:9px;padding:0 12px;background:#fff}.auto-dispatch-controls button{color:#08746d;font-weight:800}.auto-dispatch-controls button.enable-auto{color:#fff;background:#0b8379}.auto-dispatch-controls button.disable-auto{border-color:#d99b92;color:#9b2c22;background:#fff3f1}@media(max-width:700px){.auto-dispatch-settings header{display:grid}.auto-dispatch-controls>*{flex:1}}
.publication-proofs{display:grid;gap:8px;margin-top:12px;padding:12px;border:1px solid #cfe1e3;border-radius:9px;background:#f8fbfb}.publication-proofs>header{display:flex;justify-content:space-between;gap:12px}.publication-proofs>header span{color:#71868d;font-size:12px}.publication-proof{display:grid;gap:6px;padding:10px;border-radius:8px;background:#fff}.publication-proof>div{display:flex;justify-content:space-between}.publication-proof a{overflow-wrap:anywhere;color:#08746d}.publication-proof footer{display:flex;gap:8px}.publication-proof button{min-height:34px;border:1px solid #82bdb7;border-radius:8px;color:#08746d;background:#eff9f7;font-weight:800}.publication-proof button.reject-proof{border-color:#d99b92;color:#9b2c22;background:#fff3f1}.publication-proof button:disabled{opacity:.55}.proof-passed{color:#187647}.proof-rejected{color:#a32a22}.proof-submitted,.proof-checking,.proof-manual_review_required{color:#805b0b}@media(max-width:700px){.publication-proofs>header,.publication-proof footer{display:grid}}
.content-lineage{margin-top:12px;padding:12px;border:1px solid #c8dce7;border-radius:9px;background:#f5fafe}.content-lineage summary{cursor:pointer;color:#205e77;font-weight:900}.lineage-order{color:#315f70!important;font-weight:800}.lineage-pages{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin-top:10px}.lineage-pages article{padding:10px;border-radius:8px;background:#fff}.lineage-pages header{display:flex;flex-wrap:wrap;gap:8px;align-items:center}.lineage-pages header span{padding:3px 7px;border-radius:999px;color:#805b0b;background:#fff3d2;font-size:12px}.lineage-pages p{color:#526970!important;line-height:1.55}.lineage-pages b{color:#294b57}@media(max-width:700px){.lineage-pages{grid-template-columns:1fr}}
.content-review-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:10px;padding:10px;border-radius:8px;color:#315f70;background:#e7f4f8}.content-review-toolbar span{font-size:13px;line-height:1.5}.content-review-toolbar button,.content-review-actions button{min-height:34px;padding:0 10px;border:1px solid #86b9c8;border-radius:8px;color:#205e77;background:#fff;font-weight:800}.content-review-actions{display:flex;flex-wrap:wrap;gap:7px;margin-top:10px}.content-review-actions button:disabled{cursor:not-allowed;opacity:.48}.latest-app-content{margin-top:10px;padding:12px;border:1px solid #cae0d3;border-radius:9px;background:#f4fbf6}.latest-app-content summary{cursor:pointer;color:#246a45;font-weight:900}.latest-app-content p{white-space:pre-wrap;line-height:1.55}.latest-app-content small{color:#5e7568}@media(max-width:700px){.content-review-toolbar{display:grid}.content-review-toolbar button,.content-review-actions button{width:100%}}
</style>
